<script setup lang="ts">
import type { AnimatedNumber } from '#components'

interface BadgeContent {
  avatar: {
    alt: string
    src: string
  }
  color: UBadgeColor
  column: number
  count: number
  icon: string
  key: string
  label: string
  timeout: ReturnType<typeof useRafTimeout>
}

const { t } = useI18n()
const animatedNumber0 = useTemplateRef<typeof AnimatedNumber>('animatedNumber-0')
const animatedNumber1 = useTemplateRef<typeof AnimatedNumber>('animatedNumber-1')
const animatedNumber2 = useTemplateRef<typeof AnimatedNumber>('animatedNumber-2')
const container = useTemplateRef<HTMLDivElement>('container')
const isVisible = useElementVisibility(container)

const lastBadgeSelected = ref<string>()
const lastButtonAdded = ref<number>()

const lists = [
  {
    avatar: {
      alt: 'The Companies API',
      src: getLogo('thecompaniesapi.com', 'companies'),
    },
    badge: ref<BadgeContent | undefined>(),
    count: ref(Math.floor(Math.random() * (10000 - 1000) + 1000)),
    isNumberAnimating: computed(() => {
      return animatedNumber0.value?.isAnimating
    }),
    title: t('columns.payingCustomers'),
  },
  {
    avatar: {
      alt: 'Instagram',
      src: getLogo('instagram.com', 'companies'),

    },
    badge: ref<BadgeContent | undefined>(),
    count: ref(Math.floor(Math.random() * (10000 - 1000) + 1000)),
    isNumberAnimating: computed(() => {
      return animatedNumber1.value?.isAnimating
    }),
    title: t('columns.activeInstagram'),
  },
  {
    avatar: {
      alt: 'HubSpot',
      src: getLogo('hubspot.com', 'companies'),
    },
    badge: ref<BadgeContent | undefined>(),
    count: ref(Math.floor(Math.random() * (10000 - 1000) + 1000)),
    isNumberAnimating: computed(() => {
      return animatedNumber2.value?.isAnimating
    }),
    title: t('columns.hubspotUsers'),
  },
] as const

function generateRandomBadge(targetList: {
  badge: Ref<BadgeContent | undefined>
  count: Ref<number>
  isNumberAnimating: Ref<boolean>
  title: string
}, column: number): BadgeContent {
  if (targetList.badge.value?.timeout) {
    targetList.badge.value.timeout.stop()
  }

  const count = Math.floor(Math.random() * 1000)

  const badges: (UBadge & { count: number })[] = [
    {
      color: 'primary',
      count,
      icon: getIcon('api'),
      key: 'api',
    },
    {
      color: 'purple',
      count,
      icon: getIcon('fileCode'),
      key: 'csv',
    },
    {
      color: 'blue',
      count,
      icon: getIcon('search'),
      key: 'search',
    },
    {
      color: 'green',
      count,
      icon: getIcon('signals'),
      key: 'signals',
    },
  ]

  // Always pickup another type of badge than the last one
  const filteredBadges = badges.filter(badge => badge.key !== lastBadgeSelected.value)

  const badge = filteredBadges[Math.floor(Math.random() * filteredBadges.length)]

  lastBadgeSelected.value = badge.key

  return {
    ...(badge as unknown as BadgeContent),
    column,
    timeout: useRafTimeout(
      () => {
        targetList.count.value += badge.count
        targetList.badge.value = undefined
      },
      2000,
      { immediate: true },
    ),
  }
}

function setRandomButton() {
  // Avoid spamming
  if (lastButtonAdded.value && (Date.now() - lastButtonAdded.value) < 1000) {
    return
  }

  lastButtonAdded.value = Date.now()

  // Always pickup list with lowest count
  const list = lists.filter(list => list.badge.value === undefined).sort((listA, listB) => listA.count.value - listB.count.value)[0]

  // Find index of list
  const column = lists.findIndex(indexList => indexList.title === list.title)

  // Skip if badge is already set
  if (list.badge.value) {
    return
  }

  list.badge.value = generateRandomBadge(list, column)
}

useRafInterval(
  () => {
    if (isVisible.value) {
      setRandomButton()
    }
  },
  3000,
)

watch(isVisible, () => {
  if (isVisible.value) {
    setRandomButton()
  }
})
</script>

<template>
  <div
    ref="container"
    class="relative flex size-full min-h-96 flex-col items-center justify-center p-4"
  >
    <div class="grid w-full grid-cols-3 grid-rows-2 gap-6">
      <template
        v-for="(list, index) in lists"
        :key="index"
      >
        <TransitionVisible
          class="transition-base transform"
          :debounce="60 * 1000"
          :hidden-class="['opacity-0 translate-y-24', index === 0 ? '-rotate-12 -translate-x-24' : index === 1 ? 'translate-x-24 rotate-12' : 'rotate-0'].join(' ')"
          :visible-class="['opacity-100 translate-y-0 rotate-0 translate-x-0', index === 0 ? '' : index === 1 ? 'delay-100' : 'delay-200'].join(' ')"
        >
          <AnimatedBorder
            class="transition-base"
            :class="{
              'col-start-1 ': index === 0,
              'col-start-3 ': index === 1,
              'col-start-2 row-start-2': index === 2,
            }"
            :duration="list.isNumberAnimating ? 5500 : 6000"
            :hoverable="false"
            :opacity="list.isNumberAnimating ? 0.5 : 0.3"
            :spread="list.isNumberAnimating ? 10 : 5"
          >
            <div class="gradient-texture-reverse flex w-full select-none flex-col gap-1 overflow-hidden rounded-xl bg-gray-50 px-4 py-3 text-xs ring-1 ring-gray-200 dark:bg-gray-950 dark:ring-gray-800">
              <div class="flex items-center justify-center gap-1.5 truncate font-bold sm:justify-start">
                <UAvatar
                  v-if="list.avatar"
                  :alt="list.avatar.alt"
                  class="rounded"
                  img-class="rounded"
                  size="3xs"
                  :src="list.avatar.src"
                />

                <span class="hidden sm:inline">{{ list.title }}</span>
              </div>

              <div class="flex items-center justify-center gap-1 sm:justify-start">
                <AnimatedNumber
                  :ref="`animatedNumber-${index}`"
                  :decimal-places="0"
                  transition="easeOutCubic"
                  :value="list.count.value"
                />

                <span class="hidden sm:inline sm:lowercase">{{ t('globals.company', { count: list.count.value }) }}</span>
              </div>
            </div>
          </AnimatedBorder>
        </TransitionVisible>
      </template>

      <div class="empty-cell col-start-2 row-start-1" />

      <div class="empty-cell col-start-1 row-start-2" />

      <div class="empty-cell col-start-3 row-start-2" />
    </div>

    <div class="absolute bottom-0 left-0 grid w-full grid-cols-3 items-center gap-2 p-2">
      <div
        v-for="(list, index) in [lists[0], lists[2], lists[1]]"
        :key="`badge-${index}`"
        class="flex items-center justify-center"
      >
        <Transition
          enter-active-class="transition-base"
          enter-from-class="opacity-0 translate-y-full"
          enter-to-class="opacity-100"
          leave-active-class="transition-base"
          leave-from-class="opacity-100 translate-y-[-42px] scale-100"
          leave-to-class="opacity-0 translate-y-[-240px] scale-50"
        >
          <UBadge
            v-if="list.badge.value"
            class="flex translate-y-[-42px] items-center justify-center truncate !px-2"
            :color="list.badge.value.color"
            :icon="list.badge.value.icon"
            :ui="{ icon: { base: 'hidden md:block' } }"
            variant="subtle"
          >
            <span class="hidden sm:inline">{{ t(`badges.${list.badge.value.key}`, { count: list.badge.value.count }) }}</span>

            <span class="flex items-center gap-1 sm:hidden">
              <UIcon
                class="size-3"
                :name="getIcon('company')"
              />

              {{ list.badge.value.count }}
            </span>
          </UBadge>
        </Transition>
      </div>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "badges": {
      "api": "Imported {count} via API",
      "csv": "Imported {count} via CSV",
      "search": "Saved {count} from search",
      "signals": "Signals discovered {count}"
    },
    "central": {
      "node": "Your lists"
    },
    "columns": {
      "activeInstagram": "Active on Instagram",
      "hubspotUsers": "HubSpot users",
      "payingCustomers": "Paying customers"
    }
  },
  "es": {
    "badges": {
      "api": "Importados {count} vía API",
      "csv": "Importados {count} vía CSV",
      "search": "Guardados {count} de la búsqueda",
      "signals": "Señales descubiertas {count}"
    },
    "central": {
      "node": "Tus listas"
    },
    "columns": {
      "activeInstagram": "Activo en Instagram",
      "hubspotUsers": "Usuarios de HubSpot",
      "payingCustomers": "Clientes pagos"
    }
  },
  "fr": {
    "badges": {
      "api": "Importé {count} via API",
      "csv": "Importé {count} via CSV",
      "search": "Enregistré {count} depuis la recherche",
      "signals": "Signaux découverts {count}"
    },
    "central": {
      "node": "Vos listes"
    },
    "columns": {
      "activeInstagram": "Actif sur Instagram",
      "hubspotUsers": "Utilisateurs de HubSpot",
      "payingCustomers": "Clients payants"
    }
  }
}
</i18n>
